import React, { useEffect, useState } from "react";
import Modal from "react-responsive-modal";
import { Ban } from "lucide-react";
import { SpinnerCircularFixed } from "spinners-react";
import { PhoneNumberStep } from "../PartialCancellation/PhoneNumberStep";
import {
  RestockSelectionProduct,
  RestockSelectionStep,
} from "../PartialCancellation/RestockSelectionStep";

export interface CancellationModalProps {
  closeModal: () => void;
  isLoading: boolean;
  makeRefund: () => Promise<void>;
  open: boolean;
  orderCode: string;
  entityLabel?: string;
  requiresPhoneNumber?: boolean;
  cardRefundTotal?: number;
  onPhoneNumberChange?: (val: string) => void;
  comment?: string;
  onCommentChange?: (val: string) => void;
  commentRequired?: boolean;
  /**
   * Productos que se cancelarán; cuando se provee, el modal muestra el paso de reinventario
   * por producto. La decisión se reporta via {@link CancellationModalProps.onRestockChange}.
   */
  restockProducts?: RestockSelectionProduct[];
  /** Mapa uuid → decisión. Si un uuid no está presente se asume `true` (reinventariar). */
  restockDecisions?: Record<string, boolean>;
  /** Callback al togglear la decisión de reinventario de un producto. */
  onRestockChange?: (uuid: string, restock: boolean) => void;
}

export const CancellationModal: React.FC<CancellationModalProps> = ({
  closeModal,
  isLoading,
  makeRefund,
  open,
  orderCode,
  entityLabel = "orden",
  requiresPhoneNumber = false,
  cardRefundTotal = 0,
  comment = '',
  onCommentChange,
  onPhoneNumberChange,
  commentRequired = true,
  restockProducts,
  restockDecisions = {},
  onRestockChange,
}) => {
  const [phoneMatches, setPhoneMatches] = useState(false);

  useEffect(() => {
    if (!open) {
      setPhoneMatches(false);
      onPhoneNumberChange?.('');
      onCommentChange?.('');
    }
  }, [open]);

  const confirmDisabled = isLoading || (commentRequired && !comment.trim()) || (requiresPhoneNumber && !phoneMatches);

  return (
    <Modal
      classNames={{
        modal: `bg-white w-auto sm:w-[550px] rounded-2xl shadow-modal !p-0`,
        overlay: `bg-modal-overlay`,
      }}
      showCloseIcon={false}
      open={open}
      onClose={closeModal}
      center
      focusTrapped={false}
    >
      <div className="p-8 sm:p-8 sm:pb-6">
        <h1 className="border-b border-gray-200 pb-4 text-xl font-bold text-gray-900">
          Confirmar cancelación
        </h1>
        <div className="mb-6 mt-6 flex flex-col gap-2">
          <h2 className="font-semibold text-gray-900">
            Advertencia importante:
          </h2>
          <p className="text-gray-600">
            Esta acción cancelará la {entityLabel}{" "}
            <span className="font-bold text-black">{orderCode}</span> de forma
            irreversible y no podrá ser recuperada después de confirmar.
          </p>
        </div>
        {restockProducts && restockProducts.length > 0 && onRestockChange && (
          <div className="mb-6">
            <h2 className="mb-2 font-semibold text-gray-900">
              ¿Regresar productos al inventario?
            </h2>
            <RestockSelectionStep
              products={restockProducts}
              decisions={restockDecisions}
              onChange={onRestockChange}
            />
          </div>
        )}
        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-gray-900">
            Motivo de cancelación{!commentRequired && <span className="ml-1 font-normal text-gray-400">(opcional)</span>}
          </label>
          <textarea
            className="w-full rounded-lg border border-gray-200 p-3 text-sm text-gray-900 placeholder-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            rows={3}
            maxLength={500}
            placeholder="Escribe el motivo de la cancelación..."
            value={comment}
            onChange={(e) => onCommentChange?.(e.target.value)}
          />
        </div>
        {requiresPhoneNumber && (
          <div className="mb-6">
            <PhoneNumberStep
              onPhoneNumberChange={(val) => onPhoneNumberChange?.(val)}
              onMatchChange={(matches) => setPhoneMatches(matches)}
              refundTotal={cardRefundTotal}
            />
          </div>
        )}
        <div className="flex flex-col justify-end gap-3 sm:flex-row">
          <button
            className="h-12 rounded-lg border border-gray-200 bg-white px-6 py-2 text-sm text-gray-900 transition-colors duration-300 hover:bg-gray-100"
            onClick={closeModal}
          >
            Descartar
          </button>
          <button
            className="flex h-12 items-center justify-center gap-2 rounded-lg bg-red-600 px-6 py-2 text-sm text-white transition-colors duration-300 hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={confirmDisabled}
            onClick={makeRefund}
          >
            {isLoading ? (
              <SpinnerCircularFixed
                size={16}
                color="#FFFFFF"
                secondaryColor="#000000"
              />
            ) : (
              <Ban className="h-4 w-4 shrink-0" />
            )}
            Confirmar cancelación
          </button>
        </div>
      </div>
    </Modal>
  );
};
